const normalizeSizeQuery = (value) =>
    String(value ?? "")
        .toLowerCase()
        .replaceAll("×", "x")
        .replaceAll(" ", "");

export const boxSizeMatches = (preset, query) => {
    const normalizedQuery = normalizeSizeQuery(query);

    if (normalizedQuery === "") {
        return true;
    }

    return (
        normalizeSizeQuery(preset?.dimensions).includes(normalizedQuery) ||
        normalizeSizeQuery(preset?.label).includes(normalizedQuery)
    );
};

export const shouldShowBoxSizes = (presets, query) =>
    normalizeSizeQuery(query) !== "" && presets.some((preset) => boxSizeMatches(preset, query));

export const boxSizeListShouldOpen = (isOpen, presetCount) => !isOpen && presetCount > 0;

export const matchingBoxSizes = (presets, query) => presets.filter((preset) => boxSizeMatches(preset, query));

export const boxSizeGroupHasMatches = (presets, query, kind) =>
    matchingBoxSizes(presets, query).some((preset) => (preset?.kind ?? "template") === kind);

export const moveBoxSizeIndex = (count, index, direction) => {
    if (count <= 0) {
        return 0;
    }

    const current = Number.isInteger(index) ? index : 0;

    return (current + direction + count) % count;
};

export const registerBoxSizePicker = (Alpine) => {
    Alpine.data("boxSizePicker", () => ({
        open: false,
        query: "",
        presets: [],
        activeIndex: 0,
        ignoreNextChange: false,

        init() {
            try {
                const parsed = JSON.parse(this.$el.dataset.presets || "[]");
                this.presets = Array.isArray(parsed) ? parsed : [];
            } catch {
                this.presets = [];
            }
        },

        onType(value) {
            this.query = value;
            this.open = shouldShowBoxSizes(this.presets, value);
            this.activeIndex = 0;
        },

        toggle() {
            if (!boxSizeListShouldOpen(this.open, this.presets.length)) {
                this.close();

                return;
            }

            this.query = "";
            this.activeIndex = 0;
            this.open = true;
            this.$root.querySelector("input")?.focus();
        },

        onKeydown(event, boxId) {
            if (!this.open) {
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.activeIndex = moveBoxSizeIndex(this.matchingPresets().length, this.activeIndex, 1);
                this.scrollActiveOption();

                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();
                this.activeIndex = moveBoxSizeIndex(this.matchingPresets().length, this.activeIndex, -1);
                this.scrollActiveOption();

                return;
            }

            if (event.key === "Enter") {
                event.preventDefault();
                this.acceptActive(boxId);
            }
        },

        matchingPresets() {
            return matchingBoxSizes(this.presets, this.query);
        },

        groupHasMatches(kind) {
            return boxSizeGroupHasMatches(this.presets, this.query, kind);
        },

        activePreset() {
            const matches = this.matchingPresets();

            if (matches.length === 0) {
                return null;
            }

            return matches[moveBoxSizeIndex(matches.length, this.activeIndex, 0)];
        },

        isActive(templateId) {
            const active = this.activePreset();

            return active !== null && String(active.id) === String(templateId);
        },

        acceptActive(boxId) {
            const preset = this.activePreset();

            if (preset === null) {
                return;
            }

            this.choose(boxId, preset.id, preset.dimensions, preset.kind, preset.package_code, preset.label);
        },

        scrollActiveOption() {
            this.$nextTick(() => {
                this.$root.querySelector('[role="option"][aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
            });
        },

        close() {
            this.open = false;
            this.query = "";
        },

        choose(boxId, templateId, dimensions, kind = "template", packageCode = "", label = "") {
            // Blur can save the half-typed text after a preset is chosen and leave only the CBM.
            this.ignoreNextChange = true;
            this.close();

            const input = this.$root.querySelector("input");
            const display = kind === "fedex_package" ? label : dimensions;

            if (input instanceof HTMLInputElement) {
                input.value = display;
                input.blur();
            }

            if (kind === "fedex_package") {
                this.$wire.applyFedExPackage(boxId, packageCode);
            } else {
                this.$wire.applyBoxTemplate(boxId, templateId);
            }

            queueMicrotask(() => {
                this.ignoreNextChange = false;
            });
        },

        commitDimensions(boxId, value) {
            if (this.ignoreNextChange) {
                this.ignoreNextChange = false;

                return;
            }

            const normalized = normalizeSizeQuery(value);
            const packagePreset = this.presets.find((preset) => {
                return preset?.kind === "fedex_package"
                    && (normalizeSizeQuery(preset.label) === normalized || normalizeSizeQuery(preset.package_code) === normalized);
            });

            if (packagePreset) {
                this.$wire.applyFedExPackage(boxId, packagePreset.package_code);

                return;
            }

            this.$wire.updateBoxField(boxId, "dimensions", value);
        },

        matches(dimensions, label) {
            return boxSizeMatches({ dimensions, label }, this.query);
        },
    }));
};
